import { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { CartContext } from '@/context/CartContext';
import { usePhoneValidation } from '@/hooks/usePhoneValidation';
import ScrollReveal from '@/components/base/ScrollReveal';

export default function CartPage() {
  const {
    items,
    itemCount,
    subtotal,
    removeItem,
    updateQuantity,
  } = useContext(CartContext);

  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [nameError, setNameError] = useState('');
  const [cityError, setCityError] = useState('');
  const [toastMessage, setToastMessage] = useState('');

  const { phone, error: phoneError, touched: phoneTouched, isValid: phoneValid, handlePhoneChange, handleBlur: handlePhoneBlur, persistPhone, displayPhone } = usePhoneValidation();

  const WhatsAppNumber = '919686543847';

  const buildWhatsAppMessage = () => {
    const lineItems = items
      .map(
        (item) =>
          `${item.name}${item.variant ? ` (${item.variant})` : ''} x${item.quantity} \u2013 \u20B9${item.price * item.quantity}`
      )
      .join('\n');

    return `Hi Joviaa! I'd like to order:\n\n${lineItems}\n\nSubtotal: \u20B9${subtotal}\nName: ${name || '(not provided)'}\nPhone: ${displayPhone || '(not provided)'}\nDelivery City/Pincode: ${city || '(not provided)'}\n\nPlease confirm availability and final pricing.`;
  };

  const handleCheckout = () => {
    // Validate all required fields
    let hasError = false;

    if (!name.trim()) {
      setNameError('Please enter your name.');
      hasError = true;
    }

    if (!phone || !phoneValid) {
      handlePhoneBlur();
      hasError = true;
    }

    if (!city.trim()) {
      setCityError('Please enter your city or pincode for delivery.');
      hasError = true;
    }

    if (hasError) return;

    // Persist phone for next visit
    persistPhone();

    const message = buildWhatsAppMessage();
    const url = `https://wa.me/${WhatsAppNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
    setToastMessage('Opening WhatsApp \u2014 please send the message to complete your order.');
    setTimeout(() => setToastMessage(''), 5000);
  };

  const handleRemove = (productId: string) => {
    removeItem(productId);
  };

  if (items.length === 0) {
    return (
      <main className="min-h-screen bg-background-50">
        {/* Breadcrumb */}
        <div className="bg-primary-500">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
            <nav className="flex items-center gap-2 text-sm font-label whitespace-nowrap flex-wrap">
              <Link to="/" className="text-background-50/70 hover:text-background-50 transition-colors">
                Home
              </Link>
              <span className="text-background-50/50">/</span>
              <span className="text-background-50 font-medium">Your Cart</span>
            </nav>
          </div>
        </div>

        {/* Empty State */}
        <div className="max-w-2xl mx-auto px-4 md:px-6 py-20 md:py-28 flex flex-col items-center text-center">
          <div className="w-24 h-24 rounded-full bg-accent-100 flex items-center justify-center mb-6">
            <i className="ri-shopping-bag-line text-4xl text-accent-500"></i>
          </div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-3">
            Your bag is feeling light.
          </h1>
          <p className="text-foreground-500 font-label text-sm md:text-base mb-8 max-w-md">
            You haven&apos;t added anything yet. Browse our collections and find your next everyday gold piece.
          </p>
          <Link
            to="/shop"
            className="px-8 py-3 bg-primary-500 text-background-50 rounded-lg font-heading font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </div>
            Continue Shopping
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background-50">
      {/* Breadcrumb */}
      <div className="bg-primary-500">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-2 text-sm font-label whitespace-nowrap flex-wrap">
            <Link to="/" className="text-background-50/70 hover:text-background-50 transition-colors">
              Home
            </Link>
            <span className="text-background-50/50">/</span>
            <span className="text-background-50 font-medium">Your Cart ({itemCount} {itemCount === 1 ? 'item' : 'items'})</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-8">
          Your Cart
        </h1>

        {/* Main Grid: Line Items + Summary */}
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left: Line Items Table */}
          <div className="flex-1 min-w-0">
            {/* Table Header - hidden on mobile */}
            <div className="hidden md:grid md:grid-cols-[auto_1fr_120px_140px_40px] gap-4 px-4 pb-3 text-xs font-label font-semibold text-foreground-400 uppercase tracking-wider border-b border-background-200">
              <span className="w-20"></span>
              <span>Product</span>
              <span className="text-center">Price</span>
              <span className="text-center">Quantity</span>
              <span></span>
            </div>

            {/* Line Items */}
            <div className="flex flex-col divide-y divide-background-200">
              {items.map((item, idx) => (
                <ScrollReveal key={item.productId + (item.variant || '')} delay={idx * 80}>
                  <div
                    className="py-4 md:py-5 flex flex-col sm:flex-row sm:items-center gap-4 md:grid md:grid-cols-[auto_1fr_120px_140px_40px] md:gap-4 md:px-4"
                  >
                    {/* Product Image */}
                    <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-secondary-200">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>

                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/product/${item.productId.replace(/-\d+$/, '')}`}
                        className="text-sm font-heading font-semibold text-foreground-800 hover:text-primary-500 transition-colors line-clamp-2"
                      >
                        {item.name}
                      </Link>
                      {item.variant && (
                        <p className="text-xs text-foreground-500 mt-1">{item.variant}</p>
                      )}
                      {/* Mobile price */}
                      <p className="text-sm font-heading font-bold text-primary-500 mt-1 md:hidden">
                        ₹{item.price * item.quantity}
                      </p>
                    </div>

                    {/* Price (desktop) */}
                    <div className="hidden md:flex items-center justify-center">
                      <span className="text-sm font-heading font-semibold text-primary-500 whitespace-nowrap">
                        ₹{item.price * item.quantity}
                      </span>
                    </div>

                    {/* Quantity Stepper */}
                    <div className="flex items-center justify-center">
                      <div className="flex items-center border border-background-300 rounded-md bg-background-50">
                        <button
                          onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                          className="w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer"
                          aria-label="Decrease quantity"
                        >
                          <i className="ri-subtract-line text-sm"></i>
                        </button>
                        <span className="w-10 text-center text-sm font-label font-medium text-foreground-800">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                          className="w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer"
                          aria-label="Increase quantity"
                        >
                          <i className="ri-add-line text-sm"></i>
                        </button>
                      </div>
                    </div>

                    {/* Remove */}
                    <div className="flex md:justify-center">
                      <button
                        onClick={() => handleRemove(item.productId)}
                        className="text-foreground-400 hover:text-accent-700 transition-colors cursor-pointer"
                        aria-label="Remove item"
                      >
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-delete-bin-line"></i>
                        </div>
                      </button>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>

            {/* Continue Shopping Link */}
            <div className="mt-6">
              <Link
                to="/shop"
                className="inline-flex items-center gap-1.5 text-sm font-label font-medium text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line"></i>
                </div>
                Continue Shopping
              </Link>
            </div>
          </div>

          {/* Right: Summary Card */}
          <ScrollReveal direction="right" className="w-full lg:w-[380px] flex-shrink-0">
            <div className="lg:sticky lg:top-28">
              <div className="bg-background-50 rounded-lg border border-background-200 p-5 md:p-6">
                <h2 className="text-lg font-heading font-semibold text-primary-500 mb-5">
                  Order Summary
                </h2>

                {/* Subtotal Row */}
                <div className="flex items-center justify-between pb-4 border-b border-background-200">
                  <span className="text-sm font-label font-medium text-foreground-600">
                    Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'items'})
                  </span>
                  <span className="text-xl font-heading font-bold text-primary-500">
                    ₹{subtotal}
                  </span>
                </div>

                {/* Shipping Note */}
                <div className="flex items-start gap-2 mt-4 p-3 bg-accent-100 rounded-md">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-information-line text-sm text-accent-600"></i>
                  </div>
                  <p className="text-xs text-accent-700 font-label leading-relaxed">
                    Shipping &amp; final total to be confirmed on WhatsApp. No online payment required.
                  </p>
                </div>

                {/* Mini-Form */}
                <div className="mt-5 flex flex-col gap-3">
                  <div>
                    <label className="block text-xs font-label font-semibold text-foreground-500 mb-1.5">
                      Your Name
                    </label>
                    <input
                      type="text"
                      placeholder="Enter your name"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        if (nameError) setNameError('');
                      }}
                      className={`w-full px-3 py-2.5 text-sm font-label border rounded-md bg-background-50 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors ${nameError ? 'border-red-400' : 'border-background-300'}`}
                    />
                    {nameError && (
                      <p className="text-xs text-red-500 mt-1 font-label">{nameError}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-label font-semibold text-foreground-500 mb-1.5">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      placeholder="Enter your 10-digit mobile number"
                      value={phone}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      onBlur={handlePhoneBlur}
                      className={`w-full px-3 py-2.5 text-sm font-label border rounded-md bg-background-50 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors ${phoneTouched && phoneError ? 'border-red-400' : 'border-background-300'}`}
                    />
                    {phoneTouched && phoneError && (
                      <p className="text-xs text-red-500 mt-1 font-label">{phoneError}</p>
                    )}
                    {!phoneTouched && !phone && (
                      <p className="text-xs text-foreground-400 mt-1 font-label">We&apos;ll confirm your order on this number over WhatsApp.</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-label font-semibold text-foreground-500 mb-1.5">
                      Delivery City / Pincode
                    </label>
                    <input
                      type="text"
                      placeholder="City or pincode"
                      value={city}
                      onChange={(e) => {
                        setCity(e.target.value);
                        if (cityError) setCityError('');
                      }}
                      className={`w-full px-3 py-2.5 text-sm font-label border rounded-md bg-background-50 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors ${cityError ? 'border-red-400' : 'border-background-300'}`}
                    />
                    {cityError && (
                      <p className="text-xs text-red-500 mt-1 font-label">{cityError}</p>
                    )}
                  </div>
                </div>

                {/* WhatsApp Checkout Button */}
                <button
                  onClick={handleCheckout}
                  className="w-full mt-5 py-3.5 bg-primary-500 text-background-50 rounded-lg font-heading font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-whatsapp-fill text-lg"></i>
                  </div>
                  Checkout on WhatsApp
                </button>

                <p className="text-xs text-foreground-500 italic text-center mt-3 font-label">
                  No online payment required — we&apos;ll confirm your order details and pricing over WhatsApp.
                </p>
              </div>

              {/* Reassurance Block */}
              <div className="mt-5 bg-accent-100 rounded-lg p-5 md:p-6">
                <div className="flex flex-col gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
                      <i className="ri-chat-check-line text-sm text-background-50"></i>
                    </div>
                    <div>
                      <p className="text-sm font-heading font-semibold text-accent-800">
                        Confirmed by a real person
                      </p>
                      <p className="text-xs text-accent-600 font-label mt-0.5">
                        Our team reviews every order manually before confirming.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
                      <i className="ri-shield-check-line text-sm text-background-50"></i>
                    </div>
                    <div>
                      <p className="text-sm font-heading font-semibold text-accent-800">
                        No payment now
                      </p>
                      <p className="text-xs text-accent-600 font-label mt-0.5">
                        You only pay after we confirm your order on WhatsApp.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
                      <i className="ri-smartphone-line text-sm text-background-50"></i>
                    </div>
                    <div>
                      <p className="text-sm font-heading font-semibold text-accent-800">
                        Pay via UPI / COD once confirmed
                      </p>
                      <p className="text-xs text-accent-600 font-label mt-0.5">
                        Flexible payment options arranged directly on chat.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>

      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-[80] max-w-sm bg-accent-500 text-background-50 px-5 py-3.5 rounded-lg shadow-lg text-sm font-label font-medium animate-fade-in">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-whatsapp-fill"></i>
            </div>
            {toastMessage}
          </div>
        </div>
      )}
    </main>
  );
}